/**
 * Formatting for the limit inputs on the wallet currency form.
 *
 * Rules these exist to satisfy:
 *  - digits only: letters and symbols never make it into the value;
 *  - a decimal point is accepted when the operator types one;
 *  - but decimals are never *added* on their own, so 1000 stays "1,000" and
 *    does not become "1,000.00". That rules out antd's `precision`, which
 *    would pad every whole number.
 */

/** Strips separators and stray characters, keeping at most one decimal point. */
export const parseAmount = value => {
  const cleaned = String(value ?? '').replace(/[^\d.]/g, '');
  const [whole, ...rest] = cleaned.split('.');
  return rest.length ? `${whole}.${rest.join('')}` : whole;
};

/**
 * Groups the whole part with commas and leaves whatever follows the decimal
 * point untouched — including a lone trailing "." mid-typing, which would
 * otherwise be swallowed and make the field feel broken.
 */
export const formatAmount = value => {
  if (value === null || value === undefined || value === '') return '';
  const [whole, decimals] = String(value).split('.');
  const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decimals === undefined ? grouped : `${grouped}.${decimals}`;
};

/** Form values arrive as strings (stringMode); the API wants numbers. */
export const toNumber = value => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(String(value).replace(/,/g, ''));
  return Number.isNaN(parsed) ? undefined : parsed;
};

/** Shared props for every limit input on the form. */
export const amountInputProps = {
  min: 0,
  stringMode: true,
  controls: false,
  formatter: formatAmount,
  parser: parseAmount,
};

/**
 * Whole-number variant for the transaction limits.
 *
 * The GraphQL schema types these as Float, but the service rejects anything
 * with a fractional part — a weekly limit of 12345.67 comes back as
 * `"deposit.limit.weekly" must be an integer`. Letting the field accept a
 * decimal only to have the save fail is a trap, so the decimal point never
 * makes it into the value.
 */
export const parseWholeAmount = value =>
  String(value ?? '').replace(/[^\d]/g, '');

export const wholeAmountInputProps = {
  min: 0,
  step: 1,
  stringMode: true,
  controls: false,
  formatter: formatAmount,
  parser: parseWholeAmount,
};

/** Rule for the limit fields: catches pasted or programmatic decimals. */
export const wholeNumberRule = {
  async validator(_, value) {
    if (value === undefined || value === null || value === '') return;
    const cleaned = String(value).replace(/,/g, '');
    if (!/^\d+$/.test(cleaned)) {
      throw new Error('Whole numbers only — this limit cannot take decimals');
    }
  },
};
